import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useApiClient } from '@core/api/useApiClient';
import { queryKeys } from '@core/api/queryKeys';
import type { MeSubscription, UserProfile } from '@features/user-profile/hooks/useMe';

/**
 * Shared `mutationKey` for every billing mutation that ends in `window.location.assign` — checkout,
 * the credit-pack checkout, and the Billing Portal. `SubscribePage` counts them with
 * `useIsMutating` so it can disable its CTAs while ANY of them is redirecting, including
 * `useBuyCredits`, which is owned by `BuyCreditsButton` several components away and therefore has no
 * `isPending` the page could read. Two competing `assign()` calls from one screen is the failure
 * this prevents.
 */
export const BILLING_REDIRECT_MUTATION_KEY = ['billing', 'redirect'] as const;

/**
 * A pricing-page card (mirrors the BE `PlanResponse`). Never carries a Stripe price id —
 * `isPurchasable` is the only signal the SubscribePage needs to enable/disable a plan's CTA.
 */
export interface Plan {
  tier: 'core' | 'pro';
  name: string;
  description: string;
  features: string[];
  monthlyPriceCents: number;
  annualPriceCents: number;
  currency: string;
  aiCreditsPerMonth: number;
  isFeatured: boolean;
  isPurchasable: boolean;
}

/** Which of a plan's two Stripe prices to use — never a stored domain concept, just a literal string. */
export type Cadence = 'monthly' | 'annual';

/** Body shared by checkout-session and change-plan — `tier` is a plain string (not narrowed to
 * `Plan['tier']`) since callers pass whatever tier key the selected plan card carries. */
interface PlanSelection {
  tier: string;
  cadence: Cadence;
}

/** `GET /api/v1/billing/plans` — the active catalog for the pricing page. */
export function usePlans() {
  const api = useApiClient();
  return useQuery({
    queryKey: queryKeys.plans,
    queryFn: () => api.get<Plan[]>('/api/v1/billing/plans'),
    staleTime: 60_000,
  });
}

/** Starts a Stripe-hosted Checkout Session for the given plan/cadence and redirects the browser
 * there on success. */
export function useCreateCheckout() {
  const api = useApiClient();
  return useMutation({
    mutationKey: BILLING_REDIRECT_MUTATION_KEY,
    mutationFn: (body: PlanSelection) => api.post<{ url: string }>('/api/v1/billing/checkout-session', body),
    onSuccess: ({ url }) => window.location.assign(url),
  });
}

/**
 * The one-off AI-credit top-up pack (mirrors the BE `CreditPackResponse`). Never carries a Stripe
 * price id — `available` is the only signal the UI needs: it's false when the deployment has no
 * `Stripe:CreditPack:PriceId` configured, and the whole endpoint 404s when billing itself is dark.
 * `amountCents`/`currency` are DISPLAY only — Stripe is the source of truth for what is charged.
 */
export interface CreditPack {
  credits: number;
  amountCents: number;
  currency: string;
  available: boolean;
}

/**
 * `GET /api/v1/billing/credit-pack` — the top-up pack's size + price for the "Buy credits" button.
 *
 * `retry: false` on purpose: a 404 is the ROUTINE answer when `Stripe:Enabled` is off, and the
 * button hides on any error — retrying a settled "the feature is dark" answer three times would
 * leave the button flickering in and out for a second on every mount. `staleTime` is 5 min because
 * the pack only changes with a config redeploy.
 */
export function useCreditPack() {
  const api = useApiClient();
  return useQuery({
    queryKey: queryKeys.creditPack,
    queryFn: () => api.get<CreditPack>('/api/v1/billing/credit-pack'),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/**
 * Starts a Stripe-hosted Checkout Session for ONE credit pack (`mode: payment`, not a subscription)
 * and redirects the browser there on success — the same shape as `useCreateCheckout` above, minus
 * the plan/cadence body: the pack is whatever the server has configured, so the client picks
 * nothing. No tier requirement either: a free-tier user out of credits is exactly the target buyer.
 */
export function useBuyCredits() {
  const api = useApiClient();
  return useMutation({
    mutationKey: BILLING_REDIRECT_MUTATION_KEY,
    mutationFn: () => api.post<{ url: string }>('/api/v1/billing/credits/checkout-session'),
    onSuccess: ({ url }) => window.location.assign(url),
  });
}

/** Swaps the caller's live Stripe subscription to a new plan/cadence (202 Accepted — the tier lands
 * once the resulting webhook is processed). Invalidates `['me']` so a later `subscription.changed`
 * push (or just the next `/me` read) picks up the change. */
export function useChangePlan() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PlanSelection) => api.post<void>('/api/v1/billing/change-plan', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.me }),
  });
}

/** Opens the Stripe Billing Portal for the caller's known Stripe customer and redirects there on
 * success. Gate the entry point on `subscription.manageBillingAvailable` — the server's own
 * precondition — not on `source === 'stripe'`, which both hides invoices from a lapsed payer and
 * offers the link to callers the endpoint would 409. */
export function useOpenPortal() {
  const api = useApiClient();
  return useMutation({
    mutationKey: BILLING_REDIRECT_MUTATION_KEY,
    mutationFn: () => api.post<{ url: string }>('/api/v1/billing/portal'),
    onSuccess: ({ url }) => window.location.assign(url),
  });
}

/**
 * Writes a fresh `SubscriptionResponse` straight into the cached `['me']` profile, then invalidates
 * it. Both halves matter: the write makes the page instantly true (the user just clicked "Switch to
 * Free" and must see "Ends 12 Mar" immediately, not after a webhook round-trip through the BE's
 * entitlement cache and this app's 60s `staleTime`), and the invalidation reconciles against the
 * server once, in case anything else moved with it.
 *
 * `prev &&` rather than a synthesized profile: with no `['me']` in cache there is nothing to merge
 * INTO, and inventing a half-empty `UserProfile` would be worse than letting the refetch fill it.
 */
function writeSubscriptionToMe(qc: QueryClient, subscription: MeSubscription): void {
  qc.setQueryData<UserProfile>(queryKeys.me, prev => (prev ? { ...prev, subscription } : prev));
  qc.invalidateQueries({ queryKey: queryKeys.me });
}

/**
 * The failure counterpart of `writeSubscriptionToMe` (2026-08-16): refetch `['me']` when one of the
 * four subscription verbs is REFUSED.
 *
 * All four answer a state they disagree with by 409 rather than by an idempotent success — deliberate
 * on the BE side, because these are first-party buttons rather than webhook-redelivery sinks, and
 * re-stamping `declined_at` would rewrite the moment the user actually declined. The consequence is
 * that a 409 is not merely "that didn't work": it is positive evidence that **our cached subscription
 * is stale** — the comp was already declined in another tab, the Stripe subscription has genuinely
 * ended, someone hit the button twice. Toasting alone would leave the page asserting a state the
 * server just contradicted, and the user staring at a button that keeps failing.
 *
 * Invalidating unconditionally rather than sniffing for a 409 status: a network error refetches too,
 * which is harmless (the query is already the source of truth), and it avoids coupling this module to
 * a status-code check that would silently stop matching if the BE ever changed the code.
 */
function refetchMeAfterFailure(qc: QueryClient): void {
  qc.invalidateQueries({ queryKey: queryKeys.me });
}

/**
 * **"Switch to Free"** — `POST /api/v1/billing/cancel` (2026-08-16). Schedules the cancellation for
 * the END of the paid period (Stripe `cancel_at_period_end = true`), so nothing is lost mid-cycle:
 * the user keeps Pro to the second `endsAt` names, then falls to Free. 200 with the fresh
 * subscription; 409 `subscriptions.no_stripe_subscription` for a free or COMPED caller, who has no
 * Stripe subscription to cancel in the first place — a comped user's "Switch to Free" is
 * `useDeclineComp` below, a different endpoint that never touches Stripe.
 *
 * Deliberately NOT keyed with `BILLING_REDIRECT_MUTATION_KEY` — this one stays on the page.
 */
export function useCancelSubscription() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<MeSubscription>('/api/v1/billing/cancel'),
    onSuccess: subscription => writeSubscriptionToMe(qc, subscription),
    onError: () => refetchMeAfterFailure(qc),
  });
}

/**
 * **"Keep Pro"** — `POST /api/v1/billing/resume` (2026-08-16). Clears a pending cancellation while
 * the paid period is still running: the user's own "cancelled, regretted it a week later, Pro is
 * still live" case, reactivated with **no new charge and no new AI credits**. 200 with the fresh
 * subscription; 409 `subscriptions.no_stripe_subscription` once Stripe has genuinely ended the
 * subscription (Stripe cannot revive a `canceled` one) — the refreshed `/me` then flips the card to
 * "Upgrade to Pro", which is the only route back.
 */
export function useResumeSubscription() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<MeSubscription>('/api/v1/billing/resume'),
    onSuccess: subscription => writeSubscriptionToMe(qc, subscription),
    onError: () => refetchMeAfterFailure(qc),
  });
}

/**
 * **"Switch to Free" for a COMPED account** — `POST /api/v1/billing/comp/decline` (2026-08-16). The
 * comp analogue of `useCancelSubscription`, and deliberately a SEPARATE endpoint rather than a
 * branch inside `/billing/cancel`: a comp is an admin grant with **no Stripe subscription behind
 * it**, so no Stripe call is made on this path at all (BE invariant I5). Calling `/billing/cancel`
 * for a comped user 409s `subscriptions.no_stripe_subscription` — that is the wrong verb, not a
 * fixable error.
 *
 * What it does NOT do is the whole point: it stamps `declined_at` and leaves `expires_at` alone, so
 * `planState` stays `'comped'` and `endsAt` is unchanged. The user keeps Pro to the exact second the
 * comp already had, then falls to Free — the user's rule, *"cancel, means that there will be no
 * renewal and it will move to free tier"*.
 *
 * 200 with the fresh subscription (`compDeclined: true`, `canDeclineComp: false`); 409
 * `subscriptions.comp_not_found` (no active comp), `comp_not_declinable` (an UNDATED comp — nothing
 * to decline, see `canDeclineComp`), or `comp_already_declined`. That last one is a 409 rather than
 * an idempotent no-op **by design** — re-stamping would rewrite the moment the user actually
 * declined — so keep the confirm dialog locked while this is pending and let the refreshed `['me']`
 * settle the truth on failure.
 *
 * Not keyed with `BILLING_REDIRECT_MUTATION_KEY`, same as cancel/resume — nothing redirects.
 */
export function useDeclineComp() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<MeSubscription>('/api/v1/billing/comp/decline'),
    onSuccess: subscription => writeSubscriptionToMe(qc, subscription),
    onError: () => refetchMeAfterFailure(qc),
  });
}

/**
 * **"Keep Pro"** for a declined comp — `POST /api/v1/billing/comp/keep` (2026-08-16), the comp
 * analogue of `useResumeSubscription`. Clears `declined_at` while the comp is still running, so the
 * grant simply carries on: no Stripe call, no new charge, and — since the end date was never moved —
 * nothing to restore but the intent.
 *
 * 200 with the fresh subscription (`compDeclined: false`, `canDeclineComp: true`); 409
 * `subscriptions.comp_not_found` once the comp itself is over (the way back is a NEW backoffice
 * grant, not this endpoint) or `comp_not_declined` if it was never declined.
 */
export function useKeepComp() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<MeSubscription>('/api/v1/billing/comp/keep'),
    onSuccess: subscription => writeSubscriptionToMe(qc, subscription),
    onError: () => refetchMeAfterFailure(qc),
  });
}
